import { useConfig, useOidcWithAdmin } from '@revenuewell/front-end-bundle';
import { debounce, uniqBy } from 'lodash';
import {
  createContext,
  Dispatch,
  FC,
  ReactElement,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useState
} from 'react';
import { IContactProfile } from '../../services/api-client/interface';
import UniversalPatientSearchService, {
  UniversalSearch
} from '../../services/universal-patient-search-service/universal-patient-search-service';
import { useLocations } from '../use-locations';
import { SearchFilter } from './types/search-filter';

interface ISearch {
  query: string;
  queryFilter: SearchFilter;
  data: IContactProfile[] | null;
  isLoading: boolean;
  hasMore: boolean;
  setSearch: (search: string) => void;
  setQueryFilter: Dispatch<React.SetStateAction<SearchFilter>>;
  loadMore: () => Promise<void>;
}

const SearchContext = createContext<ISearch | null>(null);

export const useSearch = (): ISearch => {
  const context = useContext(SearchContext);
  if (!context) {
    throw new Error('useSearch must be used within an SearchContext');
  }
  return context;
};

interface SearchProviderProps {
  children: React.ReactNode;
}

export const SearchProvider: FC<SearchProviderProps> = ({ children }): ReactElement => {
  const { config } = useConfig();
  const { oidcService } = useOidcWithAdmin();
  const { selectedLocation, locations } = useLocations();
  const [page, setPage] = useState(1);
  const [hasMore, setHasMore] = useState(false);
  const [isLoading, setLoading] = useState(false);
  const [data, setData] = useState<IContactProfile[] | null>(null);
  const [query, _setQuery] = useState('');
  const [queryFilter, setQueryFilter] = useState<SearchFilter>({ activePatientsOnly: true, area: 'Location' });

  const universalSearchService = useMemo(() => {
    if (!config || !oidcService) return null;
    return UniversalPatientSearchService.getInstance(config, oidcService);
  }, [config, oidcService]);

  // search in locations always reset page to 1 and set hasMore to true
  const search = useCallback(
    async (searchTerm: string, page = 1, pageSize = 30) => {
      setLoading(true);
      setHasMore(false);

      const locs = defineQueryLocations();
      const search: UniversalSearch = {
        searchTerm,
        page,
        networks: locs.map((l) => l.idNetwork),
        preferredLocationIds: locs.map((l) => l.id),
        sort: 'LastActivity',
        pageSize,
        status: queryFilter.activePatientsOnly ? 'Active' : undefined
      };

      try {
        const response = await universalSearchService?.search(search);
        setHasMore(!(response?.length === 0));

        if (page === 1) {
          setData(response || []);
        } else {
          setData((data) => uniqBy([...(data || []), ...(response || [])], 'id'));
        }
      } catch {
        //
      }
      setLoading(false);
    },
    [selectedLocation, queryFilter, universalSearchService]
  );

  const defineQueryLocations = useCallback(() => {
    if (queryFilter.area === 'Location') return locations.filter((l) => l.id === selectedLocation?.id);

    if (queryFilter.area === 'Network') return locations.filter((l) => l.idNetwork === selectedLocation?.idNetwork);

    return locations;
  }, [selectedLocation, locations, queryFilter]);

  useEffect(() => {
    setPage(1);

    if (query && query.length > 0) {
      search(query);
    } else {
      setData(null);
    }
  }, [query, queryFilter]);

  const setSearch = useMemo(
    () =>
      debounce(async (query: string) => {
        _setQuery(query);
      }, 500),
    []
  );

  const loadMore = useCallback(async () => {
    if (hasMore) {
      setPage((page) => page + 1);
      await search(query, page + 1);
    }
  }, [page, query, hasMore]);

  return (
    <SearchContext.Provider
      value={useMemo(
        () => ({
          query,
          queryFilter,
          setQueryFilter,
          data,
          isLoading,
          hasMore,
          setSearch,
          loadMore
        }),
        [query, data, isLoading, hasMore, setSearch, loadMore]
      )}
    >
      {children}
    </SearchContext.Provider>
  );
};
